import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { SiteLocale, toUiLocale, translateText } from './translations';

/** Port_Api Icerik.Dil kolonu formati (varchar(2)); ALL yalnizca listelemede kullanilir. */
export type ApiLanguage = 'TR' | 'EN';

const STORAGE_KEY = 'tbhport.locale';

/**
 * Adres cubugundan dil secimi: ?lang=en veya ?dil=en.
 * Eski Ingilizce site (en.tbhport.com) IIS'te https://www.tbhport.com/?lang=en
 * adresine yonlendirilir (public/web.config); parametre ilk acilista secili dili belirler.
 */
export const LOCALE_QUERY_PARAMS = ['lang', 'dil'] as const;

export function toApiLanguage(locale: SiteLocale): ApiLanguage {
  return locale === 'en' ? 'EN' : 'TR';
}

/** tr, TR, tr-TR, en-US gibi degerleri site locale'ine cevirir; taniyamazsa undefined. */
export function normalizeLocale(value?: string): SiteLocale | undefined {
  const code = (value || '').trim().toLowerCase().split(/[-_]/)[0];
  if (code === 'tr') return 'tr';
  if (code === 'en') return 'en';
  return undefined;
}

/** "?lang=en", "dil=EN&x=1" gibi sorgu dizesinden site locale'ini okur; yoksa undefined. */
export function readLocaleFromSearch(search: string): SiteLocale | undefined {
  const params = new URLSearchParams(search);
  for (const key of LOCALE_QUERY_PARAMS) {
    const found = normalizeLocale(params.get(key) || '');
    if (found) return found;
  }
  return undefined;
}

function readStoredLocale(): SiteLocale {
  try {
    return normalizeLocale(window.localStorage.getItem(STORAGE_KEY) || '') || 'tr';
  } catch {
    // gizli sekme / storage kapali
    return 'tr';
  }
}

/** Ilk acilista URL parametresi kayitli tercihi ezer; parametre yoksa kayitli tercih (yoksa TR). */
function readInitialLocale(): SiteLocale {
  return readLocaleFromSearch(window.location.search) || readStoredLocale();
}

type LanguageContextType = {
  locale: SiteLocale;
  /** Port_Api'ye gonderilen dil degeri: TR | EN */
  apiLanguage: ApiLanguage;
  /** Intl API'leri icin kultur kodu: tr-TR | en-US */
  uiLocale: string;
  setLocale: (locale: SiteLocale) => void;
  /**
   * Statik/Turkce yedek metinleri secili dile cevirir; sozlukte yoksa aynen
   * dondurur. context, ayni metnin sayfaya gore degisen karsiligi icin verilir.
   */
  t: (value?: string, context?: string) => string;
  tList: (values?: string[], context?: string) => string[];
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<SiteLocale>(readInitialLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
    try {
      window.localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // yut
    }
  }, [locale]);

  const setLocale = useCallback((next: SiteLocale) => {
    setLocaleState((current) => (current === next ? current : next));
  }, []);

  const t = useCallback(
    (value?: string, context?: string) => translateText(value, locale, context),
    [locale],
  );

  const tList = useCallback(
    (values?: string[], context?: string) =>
      (values || []).map((value) => translateText(value, locale, context)),
    [locale],
  );

  const value = useMemo(
    () => ({
      locale,
      apiLanguage: toApiLanguage(locale),
      uiLocale: toUiLocale(locale),
      setLocale,
      t,
      tList,
    }),
    [locale, setLocale, t, tList],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage LanguageProvider icinde kullanilmali');
  return ctx;
}
